import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import AdminPanel from './pages/AdminPanel';
import PaymentSuccess from './pages/PaymentSuccess';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState('dark');
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setTheme(response.data.theme_preference || 'dark');
      setLanguage(response.data.language || 'en');
    } catch (error) {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setTheme(userData.theme_preference || 'dark');
    setLanguage(userData.language || 'en');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const handleThemeChange = async (newTheme) => {
    try {
      await axios.put(`${API}/users/theme`, { theme: newTheme });
      setTheme(newTheme);
      setUser({ ...user, theme_preference: newTheme });
    } catch (error) {
      console.error('Error updating theme:', error);
    }
  };

  const handleLanguageChange = async (newLanguage) => {
    try {
      await axios.put(`${API}/users/language`, { language: newLanguage });
      setLanguage(newLanguage);
      setUser({ ...user, language: newLanguage });
    } catch (error) {
      console.error('Error updating language:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/auth"
            element={
              user ? <Navigate to="/dashboard" /> : <AuthPage onLogin={handleLogin} />
            }
          />
          <Route
            path="/dashboard"
            element={
              user ? (
                <Dashboard
                  user={user}
                  onLogout={handleLogout}
                  theme={theme}
                  onThemeChange={handleThemeChange}
                  language={language}
                  onLanguageChange={handleLanguageChange}
                />
              ) : (
                <Navigate to="/auth" />
              )
            }
          />
          <Route
            path="/admin"
            element={
              user && user.role === 'admin' ? (
                <AdminPanel
                  user={user}
                  onLogout={handleLogout}
                  theme={theme}
                  onThemeChange={handleThemeChange}
                  language={language}
                  onLanguageChange={handleLanguageChange}
                />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/payment-success"
            element={
              user ? (
                <PaymentSuccess user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/auth" />
              )
            }
          />
          <Route path="/" element={<Navigate to={user ? "/dashboard" : "/auth"} />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;